import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import CompanyHeader from "./FinancialS_Components/CompanyHeader";
import IncomeTable, { buildSections } from "./FinancialS_Components/IncomeTable";
import "./IncomeStatementPage.scss";

const normalizeKeys = (value) => {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nested]) => {
      acc[key.charAt(0).toLowerCase() + key.slice(1)] = normalizeKeys(nested);
      return acc;
    }, {});
  }
  return value;
};

export default function IncomeStatementPage() {
  const { fiscalYear } = useContext(FiscalYearContext);
  const [data, setData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);

  const formatNumber = (value) =>
    (() => {
      const numericValue = Number(value ?? 0);
      const formatted = Math.abs(numericValue).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return numericValue < 0 ? `(${formatted})` : formatted;
    })();

  const buildPdfDoc = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const cy = data?.currentYear ?? {};
    const py = data?.priorYear ?? {};
    const sections = buildSections(cy, py);
    const priorFiscalYear = Number(fiscalYear) - 1;
    const periodDate = `31.12.${fiscalYear}`;

    const bodyRows = [];
    const sectionRowIndexes = new Set();
    const totalRowIndexes = new Set();
    let rowIndex = 0;

    sections.forEach((section) => {
      bodyRows.push([{ content: section.title, colSpan: 5 }]);
      sectionRowIndexes.add(rowIndex);
      rowIndex += 1;

      section.items.forEach((item) => {
        bodyRows.push([
          item.code || "",
          item.label || "",
          item.note || "",
          formatNumber(item.currentVal),
          formatNumber(item.priorVal),
        ]);

        if (item.isTotal) {
          totalRowIndexes.add(rowIndex);
        }
        rowIndex += 1;
      });
    });

    doc.setTextColor(25, 25, 25);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(companyInfo?.CompanyName || "Kompania", 105, 18.5, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`Pasqyra e te ardhurave me date ${periodDate}`, 105, 23.5, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Statement of Comprehensive Income", 105, 27.5, { align: "center" });

    doc.setFontSize(8);
    doc.text(`Numri fiskal: ${companyInfo?.IdentificationNumber || "-"}`, 10, 32.5);
    doc.text(`Periudha: ${periodDate}`, 200, 32.5, { align: "right" });

    autoTable(doc, {
      startY: 38,
      head: [["Kodi", "Pershkrimi", "Shenimi", `31.12.${fiscalYear}`, `31.12.${priorFiscalYear}`]],
      body: bodyRows,
      theme: "grid",
      styles: {
        fontSize: 7.4,
        cellPadding: 0.95,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: [33, 33, 33],
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        halign: "center",
        valign: "middle",
        fontStyle: "bold",
        lineColor: [40, 73, 69],
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { cellWidth: 16, halign: "center" },
        1: { cellWidth: 92 },
        2: { cellWidth: 16, halign: "center" },
        3: { cellWidth: 36, halign: "right" },
        4: { cellWidth: 36, halign: "right" },
      },
      didParseCell: (hookData) => {
        const currentRowIndex = hookData.row.index;

        if (sectionRowIndexes.has(currentRowIndex)) {
          hookData.cell.styles.fillColor = [233, 241, 237];
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.halign = "left";
          hookData.cell.styles.textColor = [31, 58, 54];
        }

        if (totalRowIndexes.has(currentRowIndex)) {
          hookData.cell.styles.fillColor = [245, 249, 247];
          hookData.cell.styles.fontStyle = "bold";
        }
      },
      margin: { top: 8, right: 6, bottom: 8, left: 6 },
      pageBreak: "auto",
    });

    let signatureY = (doc.lastAutoTable?.finalY || 40) + 20;
    const pageHeight = doc.internal.pageSize.getHeight();

    if (signatureY > pageHeight - 22) {
      doc.addPage();
      signatureY = 36;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text("Pergatiti", 42, signatureY - 7, { align: "center" });
    doc.text("Miratoi", 148, signatureY - 7, { align: "center" });
    doc.line(20, signatureY, 85, signatureY);
    doc.line(115, signatureY, 180, signatureY);

    const totalPages = doc.internal.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Faqe ${page}/${totalPages}`, 6, 292);
      doc.text("Gjeneruar nga Accountree", 200, 292, { align: "right" });
    }

    const companySlug = (companyInfo?.CompanyName || "Kompania")
      .trim()
      .replace(/\s+/g, "_");

    return { doc, companySlug };
  };

  const handleDownloadPdf = async () => {
    if (isGeneratingPdf || isLoading || !data) {
      return;
    }
    setIsGeneratingPdf(true);
    try {
      const { doc, companySlug } = buildPdfDoc();
      doc.save(`Pasqyra_e_te_ardhurave_${fiscalYear}_${companySlug}.pdf`);
    } catch (pdfError) {
      console.error("Gabim gjatë gjenerimit të PDF:", pdfError);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintPdf = async () => {
    if (isGeneratingPrint || isLoading || !data) {
      return;
    }
    setIsGeneratingPrint(true);
    try {
      const { doc } = buildPdfDoc();
      const blobUrl = doc.output("bloburl");

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      };
    } catch (printError) {
      console.error("Gabim gjatë printimit:", printError);
    } finally {
      setIsGeneratingPrint(false);
    }
  };

  useEffect(() => {
    const fetchIncomeStatement = async () => {
      if (!fiscalYear || fiscalYear <= 0) {
        setError("Viti fiskal nuk është përcaktuar saktë.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const headers = authHeader();
        const [incomeResponse, companyResponse] = await Promise.all([
          axios.get(ApiLink.API_URL + "BalanceSheet/getIncomeStatement", {
            headers,
            params: { fiscalYear },
          }),
          axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", {
            headers,
          }),
        ]);

        setData(incomeResponse.data ? normalizeKeys(incomeResponse.data) : null);

        if (Array.isArray(companyResponse.data) && companyResponse.data.length > 0) {
          setCompanyInfo(companyResponse.data[0]);
        } else {
          setCompanyInfo(null);
        }
      } catch (err) {
        console.error("Gabim në marrjen e pasqyrës së të ardhurave:", err);
        setError(
          err.response?.data?.message || "Gabim në marrjen e të dhënave"
        );
        setData(null);
        setCompanyInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchIncomeStatement();
  }, [fiscalYear]);

  if (error) {
    return (
      <div className="IS__Page__Container">
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            color: "#856404",
          }}
        >
          <h3>Gabim</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="IS__Page__Container">
      <div className="IS__Page__HeaderData">
        <CompanyHeader
          reportTitle={`Pasqyra e të ardhurave gjithëpërfshirëse për vitin që përfundon më 31 dhjetor ${fiscalYear}`}
          reportSubtitle="Statement of Comprehensive Income"
          fiscalYear={fiscalYear}
          onDownloadPdf={handleDownloadPdf}
          isGeneratingPdf={isGeneratingPdf}
          onPrint={handlePrintPdf}
          isGeneratingPrint={isGeneratingPrint}
        />
      </div>

      <hr className="IS__Page__Divider" />

      <div className="IS__Page__TableWrap">
        <IncomeTable
          currentYear={data?.currentYear}
          priorYear={data?.priorYear}
          fiscalYear={Number(fiscalYear)}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
